import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
} from "react";

interface CMSSettings {
  showLatestBlog: boolean;
  showUpcomingEvents: boolean;
  showFeaturedGallery: boolean;
  blogPostCount: number;
  eventCount: number;
  recentShowsDays: number;
  // Store settings
  enableStoreSorting: boolean;
  defaultStoreSort:
    | "name"
    | "price-low"
    | "price-high"
    | "newest"
    | "best-selling";
  storeProductsPerPage: number;
  enableStoreFiltering: boolean;
}

interface CMSContextType {
  settings: CMSSettings;
  updateSettings: (newSettings: Partial<CMSSettings>) => void;
  toggleSetting: (key: keyof CMSSettings) => void;
}

const defaultSettings: CMSSettings = {
  showLatestBlog: true,
  showUpcomingEvents: true, // ✅ Enabled by default
  showFeaturedGallery: true, // ✅ Enabled by default
  blogPostCount: 3,
  eventCount: 3,
  recentShowsDays: 2,
  // Store settings
  enableStoreSorting: true,
  defaultStoreSort: "name",
  storeProductsPerPage: 24,
  enableStoreFiltering: true,
};

const CMSContext = createContext<CMSContextType | undefined>(undefined);

export const CMSProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [settings, setSettings] = useState<CMSSettings>(() => {
    // Load from localStorage if available
    const saved = localStorage.getItem("eruption-cms-settings");
    return saved
      ? { ...defaultSettings, ...JSON.parse(saved) }
      : defaultSettings;
  });

  const updateSettings = useCallback(
    (newSettings: Partial<CMSSettings>) => {
      const updated = { ...settings, ...newSettings };
      setSettings(updated);
      localStorage.setItem("eruption-cms-settings", JSON.stringify(updated));
    },
    [settings]
  );

  const toggleSetting = useCallback(
    (key: keyof CMSSettings) => {
      if (typeof settings[key] === "boolean") {
        updateSettings({ [key]: !settings[key] });
      }
    },
    [settings, updateSettings]
  );

  const contextValue = useMemo(
    () => ({
      settings,
      updateSettings,
      toggleSetting,
    }),
    [settings, updateSettings, toggleSetting]
  );

  return (
    <CMSContext.Provider value={contextValue}>{children}</CMSContext.Provider>
  );
};

export const useCMS = () => {
  const context = useContext(CMSContext);
  if (context === undefined) {
    throw new Error("useCMS must be used within a CMSProvider");
  }
  return context;
};
